"use client"

import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { X } from "lucide-react"

const BookAppointment = ({ isOpen, onClose }) => {
  const [formStep, setFormStep] = useState(0)
  const [appointmentBooked, setAppointmentBooked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedDoctorDays, setSelectedDoctorDays] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "",
    dob: "",
    department: "",
    doctor: "",
    appointmentDate: "",
    appointmentTime: "",
    reason: "",
    newPatient: "new",
    // Payment details
    paymentMethod: "card",
    cardNumber: "",
    cardName: "",
    expiryDate: "",
    cvv: "",
    upiId: "",
  })

  // Consultation fees based on department
  const consultationFees = {
    Cardiology: 1000,
    "Cardiothoracic Surgery": 1500,
    Neurology: 1200,
    Neurosurgery: 1800,
    Urology: 900,
    Nephrology: 1000,
    Endocrinology: 800,
    Rheumatology: 800,
    "Plastic Surgery": 1500,
    "Pediatric Surgery": 1200,
    "Medical Gastroenterology": 1200,
    "Surgical Gastroenterology": 1200,
    "General Medicine": 600,
    "General Surgery": 800,
    Orthopaedics: 800,
    "Obstetrics & Gynaecology": 800,
    Paediatrics: 700,
    "Dermatology (DVL)": 700,
    ENT: 600,
    Ophthalmology: 600,
    "Radiology & Imaging Sciences": 800,
    Psychiatry: 800,
    "Emergency & Trauma": 1000,
    "Pain Medicine": 800,
    "Sports Medicine": 900,
    "Critical Care": 1200,
    Anaesthesiology: 800,
    Dental: 500,
    Pulmonology: 800,
  }

  // Get consultation fee based on selected department
  const getConsultationFee = () => {
    return formData.department && consultationFees[formData.department] ? consultationFees[formData.department] : 800
  }

  // Calculate GST (18%)
  const calculateGST = () => {
    return getConsultationFee() * 0.18
  }

  // Calculate total amount
  const calculateTotal = () => {
    return getConsultationFee() + calculateGST()
  }

    const handleInputChange = (e) => {
      const { name, value } = e.target;

      if (name === "department") {
        setFormData({
          ...formData,
          department: value,
          doctor: "",
          appointmentDate: "",
          appointmentTime: "",
        });
      }

      else if (name === "doctor") {

        const selectedDoctor = dynamicDoctors.find(
          (doc) => doc.title.rendered === value
        );

        const availableDays =
          selectedDoctor?.acf?.available_days || [];

        setSelectedDoctorDays(availableDays);

        setFormData({
          ...formData,
          doctor: value,
          appointmentDate: "",
          appointmentTime: "",
        });
      }

      else {
        setFormData({
          ...formData,
          [name]: value,
        });
      }
    };

  const isSunday = (dateString) => {
  const date = new Date(dateString);
  return date.getDay() === 0;
};

const getAvailableTimeSlots = () => {
  const isSuperSpeciality = superSpecialityDepartments.includes(formData.department);
  const slots = isSuperSpeciality ? superSpecialityTimeSlots : timeSlots;

  if (!formData.appointmentDate) return slots;

  const selectedDate = new Date(formData.appointmentDate);
  const today = new Date();

  if (selectedDate.toDateString() !== today.toDateString()) {
    return slots;
  }

  const currentMinutes = today.getHours() * 60 + today.getMinutes();

  return slots.filter((slot) => {
    const [time, period] = slot.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return hours * 60 + minutes > currentMinutes;
  });
};

const getDayName = (dateString) => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return days[new Date(dateString).getDay()];
};

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    // Use the absolute path to your WordPress-based mailer script
    const response = await fetch('https://mamshospitals.mamscollege.com/wp-json/mams/v1/appointment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
        formType: "appointment"
      }),
    });

    // Check if the response is actually JSON
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const errorText = await response.text();
      console.error("Server returned HTML instead of JSON. Look at the message below:");
      // This logs the actual HTML so you can see if it's a 404 or 500 error
      console.log(errorText.substring(0, 500)); 
      console.error("Server returned non-JSON response. This usually means a 404 or Nginx error.");
      console.log("Response Preview:", errorText.substring(0, 200)); 
      
      alert("Server Error: The API endpoint was not found or returned an HTML page. Check server logs.");
      return;
    }

    const result = await response.json();
    console.log("API Response:", result);

    if (result.success) {
      setAppointmentBooked(true);
      const msg = `New Appointment Booking:%0AName: ${formData.firstName} ${formData.lastName}%0APhone: ${formData.phone}%0AEmail: ${formData.email}%0ADepartment: ${formData.department}%0ADoctor: ${formData.doctor || 'Any Available'}%0ADate: ${formData.appointmentDate} at ${formData.appointmentTime}%0AReason: ${formData.reason || 'N/A'}`;
      window.open(`https://wa.me/917399999981?text=${msg}`, '_blank');
    } else {
      alert(result.error || 'Failed to send email');
    }

  } catch (error) {
    console.error('Error sending appointment email:', error);
    alert('Something went wrong');
  } finally {
    setIsSubmitting(false);
  }
};

  const nextStep = () => {
    setFormStep(formStep + 1)
  }

  const prevStep = () => {
    setFormStep(formStep - 1)
  }

  const [dynamicDoctors, setDynamicDoctors] = useState([])
  const [departments, setDepartments] = useState([])
  const allDoctorsCache = useRef(null)

  useEffect(() => {
    const load = allDoctorsCache.current
      ? Promise.resolve(allDoctorsCache.current)
      : Promise.all([
          fetch("https://mamshospitals.mamscollege.com/wp-json/wp/v2/doctor?_embed&per_page=100&page=1").then(r => r.json()),
          fetch("https://mamshospitals.mamscollege.com/wp-json/wp/v2/doctor?_embed&per_page=100&page=2").then(r => r.json()),
        ]).then(([p1, p2]) => {
          allDoctorsCache.current = [...p1, ...p2]
          return allDoctorsCache.current
        })

    load
      .then(all => {
  const unique = [...new Set(
    all
      .map(d => d._embedded?.["wp:term"]?.[0]?.[0]?.name)
      .filter(Boolean)
  )]
    .filter(dept => !hiddenDepartments.includes(dept))
    .sort();

  setDepartments(unique);
})
      .catch(console.error)
  }, [])

  useEffect(() => {
  if (!formData.department || !allDoctorsCache.current) return;

  const hiddenDoctors = [
    "Dr. Gautham",
    "Dr. Anusha",// Replace with the exact doctor name
  ];

  setDynamicDoctors(
    allDoctorsCache.current.filter((d) => {
      const department = d._embedded?.["wp:term"]?.[0]?.[0]?.name;
      const doctorName = d.title.rendered;

      return (
        department === formData.department &&
        !hiddenDoctors.includes(doctorName)
      );
    })
  );
}, [formData.department]);

  const superSpecialityDepartments = [
    "Cardiology",
    "Cardiothoracic Surgery",
    "Neurology",
    "Neurosurgery",
    "Urology",
    "Nephrology",
    "Endocrinology",
    "Rheumatology",
    "Plastic Surgery",
    "Pediatric Surgery",
    "Medical Gastroenterology",
    "Surgical Gastroenterology",
  ]

  const timeSlots = [
    "09:00 AM",
    "09:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "02:00 PM",
    "02:30 PM",
    "03:00 PM",
    "03:30 PM",
    "04:00 PM",
  ]

  const superSpecialityTimeSlots = [
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "02:00 PM",
    "02:30 PM",
    "03:00 PM",
  ]

  const hiddenDepartments = [
  "Radiology",
  "Endocrinology",
  "Neurology",
];
// const hiddenAppointmentsOnMonday = [
//   "Cardiology",
//   "Cardiothoracic Surgery",
//   "Neurology",
//   "Neurosurgery",
//   "Urology",
//   "Nephrology",
//   "Endocrinology",
//   "Rheumatology",
//   "Plastic Surgery",
//   "Pediatric Surgery",
//   "Medical Gastroenterology",
//   "Surgical Gastroenterology",
//   "Psychiatry",
// ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="flex min-h-full items-start sm:items-center justify-center p-3 sm:p-4">
        <div className="relative bg-white w-full max-w-3xl rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-300 my-4 sm:my-6">
          {/* Close Button */}
          <button
          onClick={onClose}
          aria-label="Close appointment form"
          title="Close"
          className="absolute right-3 top-3 sm:right-4 sm:top-4 p-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X
            className="h-5 w-5 sm:h-6 sm:w-6"
            aria-hidden="true"
          />
        </button>

          <div className="p-4 sm:p-6 md:p-10">
          <div className="mb-6 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800">Book an Appointment</h2>
          </div>

          {appointmentBooked ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="text-teal-600 w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">Appointment Booked Successfully!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for booking an appointment with MAMS Hospitals. We've sent a confirmation email to{" "}
                {formData.email} with all the details.
              </p>
              <div className="bg-primary/5 p-6 rounded-lg mx-auto max-w-md text-left mb-8">
                <h3 className="font-semibold text-lg border-b pb-2 mb-4 text-gray-800">Appointment Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Patient:</span>
                    <span className="font-medium text-gray-800">
                      {formData.firstName} {formData.lastName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Department:</span>
                    <span className="font-medium text-gray-800">{formData.department}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Doctor:</span>
                    <span className="font-medium text-gray-800">{formData.doctor}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date & Time:</span>
                    <span className="font-medium text-gray-800">
                      {formData.appointmentDate} at {formData.appointmentTime}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={onClose}
                  className="bg-gray-100 text-gray-800 px-6 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                <button
                  className="bg-teal-600 text-white px-6 py-3 rounded-full font-medium hover:bg-teal-700 transition-colors"
                  onClick={() => {
                    setAppointmentBooked(false)
                    setFormStep(0)
                  }}
                >
                  Book Another Appointment
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* Progress Steps */}
              <div className="flex justify-between mb-6 sm:mb-8">
                <div className={`flex flex-col items-center ${formStep >= 0 ? "text-teal-600" : "text-gray-400"}`}>
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mb-1 text-sm ${
                      formStep >= 0 ? "bg-teal-600 text-white" : "bg-gray-300"
                    }`}
                  >
                    1
                  </div>
                  <span className="text-xs hidden sm:block">Patient Info</span>
                </div>
                <div className="flex-1 flex items-center">
                  <div className={`h-1 w-full ${formStep >= 1 ? "bg-teal-600" : "bg-gray-300"}`}></div>
                </div>
                <div className={`flex flex-col items-center ${formStep >= 1 ? "text-teal-600" : "text-gray-400"}`}>
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mb-1 text-sm ${
                      formStep >= 1 ? "bg-teal-600 text-white" : "bg-gray-300"
                    }`}
                  >
                    2
                  </div>
                  <span className="text-xs hidden sm:block">Details</span>
                </div>
                <div className="flex-1 flex items-center">
                  <div className={`h-1 w-full ${formStep >= 2 ? "bg-teal-600" : "bg-gray-300"}`}></div>
                </div>
                <div className={`flex flex-col items-center ${formStep >= 2 ? "text-teal-600" : "text-gray-400"}`}>
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mb-1 text-sm ${
                      formStep >= 2 ? "bg-teal-600 text-white" : "bg-gray-300"
                    }`}
                  >
                    3
                  </div>
                  <span className="text-xs hidden sm:block">Confirmation</span>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Step 1: Patient Information */}
                {formStep === 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Patient Information</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="firstName" className="text-gray-700 block text-sm font-medium">
                          First Name*
                        </label>
                        <input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 focus:border-teal-600 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="lastName" className="text-gray-700 block text-sm font-medium">
                          Last Name*
                        </label>
                        <input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 focus:border-teal-600 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-gray-700 block text-sm font-medium">
                          Email Address*
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 focus:border-teal-600 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-gray-700 block text-sm font-medium">
                          Phone Number*
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 focus:border-teal-600 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="gender" className="text-gray-700 block text-sm font-medium">
                          Gender*
                        </label>
                        <select
                          id="gender"
                          name="gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="age" className="text-gray-700 block text-sm font-medium">
                          Age*
                        </label>
                        <input
                          id="age"
                          name="age"
                          type="number"
                          min="0"
                          max="120"
                          placeholder="Enter your age"
                          value={formData.dob}
                          onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                          required
                          className="w-full border border-gray-300 focus:border-teal-600 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-gray-700 block text-sm font-medium">
                        Is this your first visit to MAMS Hospitals?
                      </label>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="new-patient"
                            name="newPatient"
                            value="new"
                            checked={formData.newPatient === "new"}
                            onChange={handleInputChange}
                            className="text-teal-600"
                          />
                          <label htmlFor="new-patient" className="text-gray-600 text-sm sm:text-base">
                            Yes, I'm a new patient
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="returning-patient"
                            name="newPatient"
                            value="returning"
                            checked={formData.newPatient === "returning"}
                            onChange={handleInputChange}
                            className="text-teal-600"
                          />
                          <label htmlFor="returning-patient" className="text-gray-600 text-sm sm:text-base">
                            No, I've been here before
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end mt-6">
                      <button
                        type="button"
                        className="w-full sm:w-auto bg-[#0A6159] text-white px-6 py-3 rounded-full font-medium hover:bg-[#084D47] transition-colors"
                        onClick={nextStep}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Department & Doctor Selection */}
                {formStep === 1 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Appointment Details</h2>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label htmlFor="department" className="text-gray-700 block text-sm font-medium">
                          Select Department*
                        </label>
                        <select
                          id="department"
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                        >
                          <option value="">
                            {departments.length === 0 ? "Loading departments..." : "Choose a department"}
                          </option>
                          {departments.map((dept) => (
                            <option key={dept} value={dept}>{dept}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="doctor" className="text-gray-700 block text-sm font-medium">
                          Select Doctor*
                        </label>
                        <select
                          id="doctor"
                          name="doctor"
                          value={formData.doctor}
                          onChange={handleInputChange}
                          disabled={!formData.department}
                          required
                          className="w-full border border-gray-300 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none disabled:bg-gray-100"
                        >
                          <option value="">
                            {!formData.department ? "First select a department" : "Choose a doctor"}
                          </option>
                          {dynamicDoctors.map((doc) => (
                            <option key={doc.id} value={doc.title.rendered}>
                              {doc.title.rendered}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label htmlFor="appointmentDate" className="text-gray-700 block text-sm font-medium">
                            Preferred Date*
                          </label>
                          <input
                            id="appointmentDate"
                            name="appointmentDate"
                            type="date"
                            value={formData.appointmentDate}
                            min={new Date().toISOString().split("T")[0]}
                            className="w-full border border-gray-300 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                            required
                          onChange={(e) => {
                            const selectedDate = e.target.value;

                            const selectedDay = getDayName(selectedDate);

                            // if (
                            //   selectedDay === "Monday" &&
                            //   hiddenAppointmentsOnMonday.includes(formData.department)
                            // ) {
                            //   alert(`Appointments are not available for ${formData.department} on Monday`);
                            //   return;
                            // }

                            if (selectedDay === "Sunday") {
                              alert("Appointments are not available on Sundays");
                              return;
                            }

                            if (
                              selectedDoctorDays.length > 0 &&
                              !selectedDoctorDays.includes(selectedDay)
                            ) {
                              alert(
                                `This doctor is available only on: ${selectedDoctorDays.join(", ")}`
                              );
                              return;
                            }

                            setFormData({
                              ...formData,
                              appointmentDate: selectedDate,
                              appointmentTime: "",
                            });
                          }}
                          />
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="appointmentTime" className="text-gray-700 block text-sm font-medium">
                            Preferred Time*
                          </label>
                          <select
                            id="appointmentTime"
                            name="appointmentTime"
                            value={formData.appointmentTime}
                            onChange={handleInputChange}
                            required
                            className="w-full border border-gray-300 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none"
                          >
                            <option value="">Select time</option>

                            {getAvailableTimeSlots().length === 0 ? (
                              <option value="" disabled>
                                No slots available today
                              </option>
                            ) : (
                              getAvailableTimeSlots().map((time) => (
                                <option key={time} value={time}>
                                  {time}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="reason" className="text-gray-700 block text-sm font-medium">
                          Reason for Visit
                        </label>
                        <textarea
                          id="reason"
                          name="reason"
                          placeholder="Please briefly describe your symptoms or reason for appointment"
                          value={formData.reason}
                          onChange={handleInputChange}
                          rows="4"
                          className="w-full border border-gray-300 focus:border-teal-600 focus:ring-teal-600 rounded-lg px-3 py-2 focus:outline-none resize-none"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 mt-6">
                      <button
                        type="button"
                        className="w-full sm:w-auto bg-gray-100 text-gray-800 px-6 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors"
                        onClick={prevStep}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="w-full sm:w-auto bg-teal-600 text-white px-6 py-3 rounded-full font-medium hover:bg-teal-700 transition-colors"
                        onClick={nextStep}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Review & Confirm */}
                {formStep === 2 && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Review & Confirm</h2>

                    <div className="space-y-6">
                      <div className="bg-primary/5 p-4 rounded-lg">
                        <h3 className="font-semibold mb-3 text-gray-800">Patient Information</h3>
                        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-y-2 text-sm">
                          <div className="text-gray-600 font-medium sm:font-normal">Name:</div>
                          <div className="text-gray-800 break-words">
                            {formData.firstName} {formData.lastName}
                          </div>
                          <div className="text-gray-600 font-medium sm:font-normal">Email:</div>
                          <div className="text-gray-800 break-all">{formData.email}</div>
                          <div className="text-gray-600 font-medium sm:font-normal">Phone:</div>
                          <div className="text-gray-800">{formData.phone}</div>
                          <div className="text-gray-600 font-medium sm:font-normal">Gender:</div>
                          <div className="capitalize text-gray-800">{formData.gender}</div>
                          <div className="text-gray-600 font-medium sm:font-normal">Age:</div>
                          <div className="text-gray-800">{formData.dob} years</div>
                          <div className="text-gray-600 font-medium sm:font-normal">Patient Type:</div>
                          <div className="text-gray-800">
                            {formData.newPatient === "new" ? "New Patient" : "Returning Patient"}
                          </div>
                        </div>
                      </div>

                      <div className="bg-primary/5 p-4 rounded-lg">
                        <h3 className="font-semibold mb-3 text-gray-800">Appointment Details</h3>
                        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-y-2 text-sm">
                          <div className="text-gray-600 font-medium sm:font-normal">Department:</div>
                          <div className="text-gray-800 break-words">{formData.department}</div>
                          <div className="text-gray-600 font-medium sm:font-normal">Doctor:</div>
                          <div className="text-gray-800 break-words">{formData.doctor || "Any Available Doctor"}</div>
                          <div className="text-gray-600 font-medium sm:font-normal">Date:</div>
                          <div className="text-gray-800">{formData.appointmentDate}</div>
                          <div className="text-gray-600 font-medium sm:font-normal">Time:</div>
                          <div className="text-gray-800">{formData.appointmentTime}</div>
                          {formData.reason && (
                            <>
                              <div className="text-gray-600 font-medium sm:font-normal">Reason for Visit:</div>
                              <div className="text-gray-800 break-words">{formData.reason}</div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="bg-primary/5 p-4 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg
                            className="text-teal-600 mt-1 w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <div className="text-sm text-gray-600">
                            By clicking "Confirm & Pay", you agree to our terms and conditions. Please arrive 15 minutes
                            before your scheduled appointment time. If you need to cancel or reschedule, please contact
                            us at least 24 hours in advance.
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 mt-6">
                      <button
                        type="button"
                        className="w-full sm:w-auto bg-gray-100 text-gray-800 px-6 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors"
                        onClick={prevStep}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full sm:w-auto bg-teal-600 text-white px-6 py-3 rounded-full font-medium hover:bg-teal-700 transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? "Confirming..." : "Confirm Appointment"}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookAppointment